<template>
  <div>
    <!-- Header -->
    <AppHeader />

    <!-- Hero Section -->
    <section class="hero hero-eastern">
      <div class="container">
        <div class="hero-content fade-in-up">
          <h1 id="contact-methods">Свяжитесь с нами</h1>
          <p>Свяжитесь с нами любым удобным для вас способом через соц.сети или отпавьте заявку через форму на сайте.</p>
        </div>
        <div class="contact-icons">
          <a v-if="contacts.show_email_on_site && contacts.email_contact" :href="`mailto:${contacts.email_contact}`" class="contact-icon" target="_blank" rel="noopener" aria-label="Email">
            <i class="fas fa-envelope"></i>
          </a>
          <a v-if="contacts.show_telegram_on_site && contacts.telegram_link" :href="contacts.telegram_link" class="contact-icon" target="_blank" rel="noopener" aria-label="Telegram">
            <i class="fab fa-telegram"></i>
          </a>
          <a v-if="contacts.show_whatsapp_on_site && contacts.whatsapp_link" :href="contacts.whatsapp_link" class="contact-icon" target="_blank" rel="noopener" aria-label="WhatsApp">
            <i class="fab fa-whatsapp"></i>
          </a>
          <a v-if="contacts.show_instagram_on_site && contacts.instagram_link" :href="contacts.instagram_link" class="contact-icon" target="_blank" rel="noopener" aria-label="Instagram">
            <i class="fab fa-instagram"></i>
          </a>
        </div>
      </div>
    </section>

    <!-- Public Application Form (optional) -->
    <UISection v-if="site.show_contact_form_on_site" id="contact-form" :title="null" class="no-title">
      <template #default>
        <form class="public-form" @submit.prevent="submitApplication">
          <div class="form-grid">
            <label>
              <span>Ваше имя</span>
              <input type="text" v-model="form.name" required placeholder="Имя" />
            </label>
            <label>
              <span>Контакт для связи</span>
              <input type="text" v-model="form.contact" required placeholder="Email, телефон или соц.сеть" />
            </label>
          </div>
          <label>
            <span>Уровень владения языком</span>
            <select v-model="form.level">
              <option value="unknown">Не знаю</option>
              <option value="none">Не учил язык</option>
              <option value="beginner">Начальный</option>
              <option value="intermediate">Средний</option>
              <option value="advanced">Высокий</option>
              <option value="fluent">Свободно говорю</option>
            </select>
          </label>
          <label class="w-full">
            <span>Сообщение</span>
            <textarea v-model="form.message" required placeholder="Коротко опишите ваш запрос"></textarea>
          </label>
          <div class="form-actions">
            <button type="submit" class="btn-primary" :disabled="loading">
              {{ loading ? 'Отправка...' : 'Отправить заявку' }}
            </button>
            <span v-if="error" class="error">{{ error }}</span>
            <span v-if="success" class="success">Спасибо! Мы свяжемся с вами.</span>
          </div>
        </form>
      </template>
    </UISection>

    <!-- Captcha Modal -->
    <div v-if="captchaModalOpen" class="modal-backdrop" @click.self="closeCaptchaModal">
      <div class="modal">
        <h3>Подождите, идет проверка, что вы не робот...</h3>
        <div id="turnstile-widget" class="turnstile-box"></div>
        <button class="btn-secondary" @click="closeCaptchaModal">Отмена</button>
      </div>
    </div>

    <!-- Contact Methods moved into hero -->

    <!-- FAQ Section -->
    <section v-if="faqs.length > 0" class="features">
      <div class="container">
        <h2 class="section-title">Часто задаваемые вопросы</h2>
        <div class="faq-grid">
          <div v-for="faq in faqs" :key="faq.id" class="faq-item">
            <h3>{{ faq.title }}</h3>
            <p>{{ faq.answer }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="cta-section">
      <div class="container">
        <h2>Готовы начать изучение китайского?</h2>
        <p>Не откладывайте на завтра то, что можно начать сегодня</p>
        <UIButton href="#contact-methods">Написать</UIButton>
      </div>
    </section>

    <!-- Footer -->
    <UIFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import UIButton from '@/components/ui/UIButton.vue'
import UISection from '@/components/ui/UISection.vue'
import UIFooter from '@/components/ui/UIFooter.vue'

export default {
  name: 'ContactsPage',
  components: {
    AppHeader,
    UIButton,
    UISection,
    UIFooter
  },
  data() {
    return {
      site: { show_contact_form_on_site: false },
      form: { name: '', contact: '', message: '', level: 'unknown', captcha_token: null },
      loading: false,
      error: '',
      success: false,
      captchaEnabled: false,
      turnstileWidget: null,
      captchaModalOpen: false,
      contacts: {
        show_email_on_site: false,
        email_contact: null,
        show_telegram_on_site: false,
        telegram_link: null,
        show_whatsapp_on_site: false,
        whatsapp_link: null,
        show_instagram_on_site: false,
        instagram_link: null,
      },
      faqs: []
    }
  },
  async mounted() {
    await Promise.all([this.loadContacts(), this.loadSiteSettings(), this.loadFAQ()])
    // Проверяем настройки капчи с сервера
    try {
      const cfgResp = await fetch('/api/v1/public/captcha-config')
      if (cfgResp.ok) {
        const cfg = await cfgResp.json()
        this.captchaEnabled = !!cfg.site_key
        if (this.captchaEnabled && this.site.show_contact_form_on_site) this.initTurnstile()
      }
    } catch (e) { /* no-op */ }
    
    // Обработка якоря после загрузки компонента
    this.$nextTick(() => {
      this.scrollToAnchor()
    })
  },
  watch: {
    '$route'(to) {
      // Обработка якоря при изменении роута на той же странице
      if (to.name === 'Contacts' && to.hash) {
        this.$nextTick(() => {
          this.scrollToAnchor()
        })
      }
    }
  },
  methods: {
    scrollToAnchor() {
      const hash = this.$route.hash
      if (hash) {
        // Убираем # из начала
        const id = hash.substring(1)
        const element = document.getElementById(id)
        if (element) {
          // Небольшая задержка для гарантии, что все элементы отрендерены
          setTimeout(() => {
            const headerOffset = 80 // Высота фиксированного хедера
            const elementPosition = element.getBoundingClientRect().top
            const offsetPosition = elementPosition + window.pageYOffset - headerOffset

            window.scrollTo({
              top: offsetPosition,
              behavior: 'smooth'
            })
          }, 100)
        }
      }
    },
    async loadSiteSettings() {
      try {
        const resp = await fetch('/api/v1/public/site-settings')
        if (resp.ok) {
          this.site = await resp.json()
        }
      } catch (e) { /* no-op */ }
    },
    async loadContacts() {
      try {
        const resp = await fetch('/api/v1/public/site-settings')
        if (resp.ok) {
          const data = await resp.json()
          this.contacts = {
            show_email_on_site: !!data.show_email_on_site,
            email_contact: data.email_contact || null,
            show_telegram_on_site: !!data.show_telegram_on_site,
            telegram_link: data.telegram_link || null,
            show_whatsapp_on_site: !!data.show_whatsapp_on_site,
            whatsapp_link: data.whatsapp_link || null,
            show_instagram_on_site: !!data.show_instagram_on_site,
            instagram_link: data.instagram_link || null,
          }
        }
      } catch (e) {
        console.error('Failed to load contacts', e)
      }
    },
    async loadFAQ() {
      try {
        const resp = await fetch('/api/v1/public/faq')
        if (resp.ok) {
          this.faqs = await resp.json()
        }
      } catch (e) {
        console.error('Failed to load FAQ', e)
      }
    },
    initTurnstile() {
      if (!this.captchaEnabled || this.turnstileWidget) return
      const fetchKey = fetch('/api/v1/public/captcha-config').then(r => r.json())
      if (!window.turnstile) {
        const script = document.createElement('script')
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
        script.async = true
        script.onload = async () => {
          const cfg = await fetchKey
          this.renderTurnstile(cfg.site_key)
        }
        document.head.appendChild(script)
      } else {
        fetchKey.then(cfg => { this.renderTurnstile(cfg.site_key) })
      }
    },
    renderTurnstile(siteKey) {
      if (!window.turnstile || this.turnstileWidget) return
      const el = document.getElementById('turnstile-widget')
      if (!el) { setTimeout(() => this.renderTurnstile(siteKey), 0); return }
      this.turnstileWidget = window.turnstile.render(el, {
        sitekey: siteKey,
        callback: (token) => { this.form.captcha_token = token; this.captchaModalOpen = false; this.submitApplication(true) },
        'expired-callback': () => { this.form.captcha_token = null },
        'error-callback': () => { this.form.captcha_token = null; this.error = 'Ошибка защиты от ботов' }
      })
    },
    openCaptchaModal() {
      this.captchaModalOpen = true
      this.$nextTick(() => this.initTurnstile())
    },
    closeCaptchaModal() {
      this.captchaModalOpen = false
    },
    async submitApplication(skipCaptcha = false) {
      // Vue передает событие как первый аргумент; нормализуем в boolean
      if (typeof skipCaptcha !== 'boolean') skipCaptcha = false
      this.error = ''; this.success = false
      // Всегда перепроверяем конфиг капчи у сервера перед отправкой
      let needCaptcha = false
      try {
        const cfgResp = await fetch('/api/v1/public/captcha-config')
        if (cfgResp.ok) {
          const cfg = await cfgResp.json()
          needCaptcha = !!cfg.site_key
          this.captchaEnabled = needCaptcha
        }
      } catch (e) { /* no-op */ }
      // Если нужна капча и токена нет — показываем модалку и останавливаем сабмит
      if (needCaptcha && !skipCaptcha && !this.form.captcha_token) {
        this.openCaptchaModal()
        return
      }
      this.loading = true
      try {
        const resp = await fetch('/api/v1/public/applications', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: this.form.name, contact: this.form.contact, message: this.form.message, level: this.form.level, captcha_token: this.form.captcha_token })
          
        })
        if (!resp.ok) {
          let detail = 'Ошибка отправки'
          try { const j = await resp.json(); detail = j?.detail || JSON.stringify(j) } catch(e) { /* no-op */ }
          throw new Error(detail)
        }
        this.success = true
        this.form.name = ''; this.form.contact = ''; this.form.message = ''
        this.form.captcha_token = null
        if (this.turnstileWidget && window.turnstile) { try { window.turnstile.reset(this.turnstileWidget) } catch(e){ /* no-op */ } }
      } catch (e) {
        this.error = e.message || 'Не удалось отправить заявку'
      } finally {
        this.loading = false
      }
    }
  }
}
</script>

<style scoped>
.nav-menu a.active {
  color: #e74c3c;
}

.public-form { display: grid; gap: .75rem; max-width: 520px; margin: 12px auto 0; }
.form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 1rem; }
.public-form label { display: flex; flex-direction: column; gap: .4rem; }
.public-form input, .public-form textarea, .public-form select { border: 1px solid #e2e8f0; border-radius: 8px; padding: .5rem .65rem; font-size: .95rem; background: #fff; }
.public-form textarea { min-height: 96px; resize: vertical; }
.form-actions { display: flex; gap: .75rem; align-items: center; }
.btn-primary { background: #e74c3c; color: #fff; border: none; border-radius: 8px; padding: .55rem 1rem; cursor: pointer; font-size: .95rem; }
.error { color: #c0392b; }
.success { color: #1f7a1f; }
.captcha-container { display: flex; justify-content: center; }

.contact-icons { display: flex; gap: 1rem; justify-content: center; margin-top: 1.25rem; }
.contact-icon { width: 48px; height: 48px; display: inline-flex; align-items: center; justify-content: center; border-radius: 9999px; background: white; border: 1px solid #e2e8f0; box-shadow: 0 4px 12px rgba(0,0,0,0.06); color: #4a5568; font-size: 20px; transition: transform .15s ease, box-shadow .15s ease, color .2s ease; text-decoration: none; }
.contact-icon:hover { transform: translateY(-2px); box-shadow: 0 8px 18px rgba(0,0,0,0.12); color: #e74c3c; }

.faq-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

.faq-item {
  background: white;
  padding: 2rem;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0,0,0,0.1);
  transition: all 0.3s ease;
}

.faq-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 30px rgba(0,0,0,0.15);
}

.faq-item h3 {
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: #2d3748;
}

.faq-item p {
  color: #718096;
  line-height: 1.6;
}

@media (max-width: 768px) {
  .contact-icons { gap: 1rem; }
  
  .faq-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-top: 3rem;
  }
}
.modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1000; }
.modal { background: #fff; border-radius: 12px; padding: 20px; min-width: 320px; max-width: 90vw; box-shadow: 0 10px 30px rgba(0,0,0,0.2); display: grid; gap: 12px; justify-items: center; }
.turnstile-box { min-height: 70px; display: flex; align-items: center; justify-content: center; }

/* Скрыть заголовки секций локально */
.no-title :deep(.section-title) { display: none; }
</style>
